import React, {
	Children,
	cloneElement,
	createContext,
	forwardRef,
	isValidElement,
	useContext,
	useEffect,
	useRef,
} from 'react'
import PropTypes from 'prop-types'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import ListSubheader from '@material-ui/core/ListSubheader'
import Divider from '@material-ui/core/Divider'
import { useTheme } from '@material-ui/core/styles'
import { VariableSizeList } from 'react-window'

const LIST_BOX_PADDING = 8

const OuterElementContext = createContext({})

const OuterElementType = forwardRef((props, ref) => {
	const outerProps = useContext(OuterElementContext)
	return <div ref={ref} {...props} {...outerProps} />
})

const ListBoxComponent = forwardRef(function ListBoxComponent(props, ref) {
	const { children, ...other } = props
	const itemData = Children.toArray(children)
	const gridRef = useRef(null)
	const theme = useTheme()
	const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true })
	const itemCount = itemData.length
	const itemSize = smUp ? 36 : 48

	const getChildSize = child => {
		if (!isValidElement(child)) {
			return itemSize
		}
		switch (child.type) {
			case Divider:
				return null
			case ListSubheader:
				return 48
			default:
				return itemSize
		}
	}

	const getHeight = () => {
		if (itemCount > 8) {
			return 8 * itemSize
		}
		return itemData.map(getChildSize).reduce((a, b) => a + b, 0)
	}

	useEffect(() => {
		if (gridRef.current != null) {
			gridRef.current.resetAfterIndex(0, true)
		}
	}, [ref])

	return (
		<div ref={ref}>
			<OuterElementContext.Provider value={other}>
				<VariableSizeList
					itemData={itemData}
					height={getHeight() + 2 * LIST_BOX_PADDING}
					width="100%"
					ref={gridRef}
					outerElementType={OuterElementType}
					innerElementType="ul"
					itemSize={index => getChildSize(itemData[index])}
					overscanCount={5}
					itemCount={itemCount}
				>
					{({ data, index, style }) => {
						return cloneElement(data[index], {
							style: {
								...style,
								top: style.top + LIST_BOX_PADDING,
							},
						})
					}}
				</VariableSizeList>
			</OuterElementContext.Provider>
		</div>
	)
})

ListBoxComponent.propTypes = {
	children: PropTypes.node.isRequired,
}

export default ListBoxComponent
